// Shared modal primitive - the team modal's shape (scrim, head, close)
// with Escape added. Mounts nothing while closed: dialog content fetches
// on mount, and a closed dialog must not issue requests.

import { useEffect, useRef, useState, type ReactNode } from "react";

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** While true the ACCIDENTAL close paths (scrim, Escape) are inert: a
   *  write is in flight and a stray click would throw away its one-time
   *  response. The head's Close stays live - a hung request must never
   *  leave the user without a way out. */
  locked?: boolean;
  /** Added to the panel: a dialog that needs its own width. */
  className?: string;
}

const CLOSE = String.fromCharCode(215);

export function Dialog({ open, ...panel }: Props) {
  return open ? <Panel {...panel} /> : null;
}

// Mounted only while open, so what it reads at mount it reads at the opening.
function Panel({ onClose, title, children, locked, className }: Omit<Props, "open">) {
  const panelRef = useRef<HTMLDivElement>(null);
  // Read before the content mounts: a field that takes focus as it mounts
  // would already hold it by the time an effect runs.
  const [opener] = useState(() => document.activeElement as HTMLElement | null);
  // A drag that starts in the panel and ends on the scrim reaches the scrim
  // as a click (the browser sends it to the common ancestor): not a close.
  const pressedPanel = useRef(false);

  // Focus the panel on open so the keyboard starts inside it. Escape is
  // read on the document: the focused control may have gone (a button
  // that unmounted, a field under hidden) and the key must still close.
  useEffect(() => {
    // A dialog that replaced another in the same commit read a field of
    // the other, gone by now; that dialog has handed focus back already.
    const back = opener?.isConnected ? opener : (document.activeElement as HTMLElement | null);
    panelRef.current?.focus();
    return () => back?.focus();
  }, [opener]);
  useEffect(() => {
    if (locked) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [locked, onClose]);

  return (
    <div
      className="dlg-scrim"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={(e) => {
        pressedPanel.current = e.target !== e.currentTarget;
      }}
      onClick={(e) => {
        if (e.target !== e.currentTarget) return;
        if (!locked && !pressedPanel.current) onClose();
        pressedPanel.current = false;
      }}
    >
      <div className={className ? `dlg ${className}` : "dlg"} ref={panelRef} tabIndex={-1}>
        <div className="dlg-head">
          <h3>{title}</h3>
          <button type="button" aria-label="Close" onClick={onClose}>
            {CLOSE}
          </button>
        </div>
        <div className="dlg-body">{children}</div>
      </div>
    </div>
  );
}
